import { Colors } from '@sigma-frontend-libs/commons'

import { numberToPxOrString, proxy } from '@/utils'

import render from './render'

const className = 'groupButton'

/**
 * Элемент списка кнопок группы.
 *
 * @typedef {Object} GroupButtonItem
 * @property {*} value Значение кнопки. Сравнивается с моделью компонента.
 * @property {string} [label] Подпись кнопки. Если не задана - берётся value.
 * @property {string} [icon] Иконка кнопки. Если задана - рисуется вместо подписи.
 * @property {string} [color] Цвет подписи выбранной кнопки. По умолчанию Colors.primary.
 * @property {boolean} [disabled] Если true - кнопка блокируется.
 * @property {string} [tooltip] Текст подсказки кнопки.
 */

/**
 * Группа кнопок с одиночным выбором. Выбранная кнопка выделяется индикатором,
 * который позиционируется по измерениям DOM и обновляется наблюдателем размера
 * контейнера кнопок. Модель компонента хранится в миксине proxy.
 */
export default {
  name: 'GroupButton',

  mixins: [ proxy() ],

  props: {
    /**
     * Список кнопок группы. Элемент - либо примитивное значение, либо объект
     * GroupButtonItem. Определяет количество и содержимое отрисованных кнопок.
     *
     * @type {Array<GroupButtonItem|*>}
     * @default []
     */
    items: {
      type: Array,
      default: () => {
        return []
      }
    },

    /**
     * Если true - группа растягивается на всю ширину родителя.
     *
     * @type {boolean}
     * @default false
     */
    fullWidth: {
      type: Boolean,
      default: false
    },

    /**
     * Если true - все кнопки получают одинаковую ширину вместо ширины по содержимому.
     *
     * @type {boolean}
     * @default false
     */
    equalWidth: {
      type: Boolean,
      default: false
    },

    /**
     * Если true - группа рисуется с рамкой и белым фоном вместо серой заливки.
     *
     * @type {boolean}
     * @default false
     */
    outline: {
      type: Boolean,
      default: false
    },

    /**
     * Если true - группа блокируется: снижается непрозрачность и не проходят клики.
     *
     * @type {boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * Если true - группа отрисовывается в обычном виде, но не реагирует на клики.
     *
     * @type {boolean}
     * @default false
     */
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      /**
       * Ширина индикатора в пикселях. Измеряется по текущей кнопке.
       *
       * @type {number}
       */
      indicatorWidth: 0,

      /**
       * Смещение индикатора слева в пикселях. Измеряется по текущей кнопке.
       *
       * @type {number}
       */
      indicatorLeft: 0
    }
  },

  computed: {
    /**
     * Возвращает классы корневого элемента по текущим модификаторам группы.
     *
     * @returns {Object} Карта классов для корневого элемента.
     */
    classes() {
      return {
        [className]: true,
        [`${className}--fullWidth`]: this.fullWidth,
        [`${className}--equalWidth`]: this.equalWidth,
        [`${className}--outline`]: this.outline,
        [`${className}--readonly`]: this.readonly,
        [`${className}--disabled`]: this.disabled
      }
    },

    /**
     * Приводит элементы items к единому виду. Примитивный элемент становится
     * одновременно значением и подписью кнопки.
     *
     * @returns {Array<GroupButtonItem>} Нормализованный список кнопок.
     */
    $_items() {
      return this.items.map(item => {
        const itemIsObject = typeof item === 'object' && !Array.isArray(item) && item !== null

        let value = item
        let label = item
        let icon
        let color = Colors.primary
        let disabled = false
        let tooltip
        if (itemIsObject) {
          value = item.value
          label = item.label ?? item.value
          icon = item.icon
          color = item.color ?? Colors.primary
          disabled = item.disabled ?? false
          tooltip = item.tooltip
        }

        return {
          value,
          label: String(label),
          icon,
          color,
          disabled,
          tooltip
        }
      })
    },

    /**
     * Возвращает индекс кнопки, значение которой совпадает с моделью.
     *
     * @returns {number} Индекс выбранной кнопки или -1, если совпадения нет.
     */
    currentIndex() {
      return this.$_items.findIndex(item => {
        return item.value === this.proxy
      })
    },

    /**
     * Признак того, что индикатор можно показывать. Ширина равна нулю, пока
     * компонент отрисован в скрытом контейнере и кнопки ещё не измерены.
     *
     * @returns {boolean} Если true - индикатор отображается.
     */
    showIndicator() {
      return this.currentIndex !== -1 && this.indicatorWidth > 0
    },

    /**
     * Возвращает инлайновые стили индикатора. Если показывать нечего - переход
     * на display none, чтобы индикатор не появлялся нулевой ширины в левом краю.
     *
     * @returns {Object} Объект стилей для элемента индикатора.
     */
    indicatorStyles() {
      if (this.showIndicator) {
        return {
          width: numberToPxOrString(this.indicatorWidth),
          transform: `translateX(${numberToPxOrString(this.indicatorLeft)})`
        }
      }

      return { display: 'none' }
    }
  },

  watch: {
    /**
     * Пересчитывает индикатор при изменении набора кнопок. Наблюдатель размера
     * такое изменение не ловит, если ширина контейнера осталась прежней.
     */
    $_items() {
      this.$nextTick(this.updateIndicator)
    },

    /**
     * Пересчитывает индикатор при смене выбранной кнопки.
     */
    currentIndex() {
      this.$nextTick(this.updateIndicator)
    }
  },

  /**
   * Подписывает индикатор на изменения размера контейнера кнопок. Наблюдатель и
   * идентификатор кадра хранятся вне data, потому что реактивность им не нужна.
   * Наблюдатель срабатывает и когда скрытый контейнер становится видимым, что
   * даёт первое корректное измерение кнопок.
   */
  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = requestAnimationFrame(this.updateIndicator)
    })
    this.resizeObserver.observe(this.$refs.holder)

    this.updateIndicator()
  },

  /**
   * Отменяет запланированный кадр и отключает наблюдатель размера, чтобы после
   * разрушения компонента не выполнялся пересчёт индикатора.
   */
  beforeDestroy() {
    cancelAnimationFrame(this.animationFrame)
    this.animationFrame = undefined

    this.resizeObserver?.disconnect()
    this.resizeObserver = undefined
  },

  methods: {
    /**
     * Измеряет текущую кнопку и переносит на неё индикатор. Если выбранной
     * кнопки нет или она ещё не отрисована - размеры сбрасываются, и индикатор
     * скрывается. Порядок $refs.buttons совпадает с порядком кнопок, пока у них
     * нет key: Vue переиспользует узлы по позиции и создаёт новые только в конце.
     */
    updateIndicator() {
      const currentButton = this.$refs.buttons?.[this.currentIndex]
      if (this.currentIndex === -1 || !currentButton) {
        this.indicatorWidth = 0
        this.indicatorLeft = 0

        return
      }

      const { offsetWidth, offsetLeft } = currentButton
      if (offsetWidth !== this.indicatorWidth) {
        this.indicatorWidth = offsetWidth
      }
      if (offsetLeft !== this.indicatorLeft) {
        this.indicatorLeft = offsetLeft
      }
    },

    /**
     * Записывает значение нажатой кнопки в модель. Если группа заблокирована,
     * доступна только для чтения или заблокирована сама кнопка - значение не
     * меняется.
     *
     * @param {GroupButtonItem} item Нажатая кнопка.
     */
    buttonClickHandler(item) {
      if (!this.disabled && !this.readonly && !item.disabled) {
        this.proxy = item.value
      }
    }
  },

  render
}
